import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Toggle } from '@actual-app/components/toggle';
import { View } from '@actual-app/components/view';

import { color, texto } from '#components/mobile/ui/tokens';
import { useLocalPref } from '#hooks/useLocalPref';

import { traspasoPendiente } from './cuentaCategoria';
import type { CuentasCategorias } from './useCuentasCategorias';

/**
 * Piezas comunes de «Cubrir» y «Mover dinero» por cuenta: el interruptor
 * «Mostrar todas las categorías» (pref local `cubrir.mostrarTodas`, la misma
 * en las dos hojas) y la línea «Recuerda hacer el traspaso…» del aviso.
 */
export function useMostrarTodas(): [boolean, (v: boolean) => void] {
  const [valor, setValor] = useLocalPref('cubrir.mostrarTodas');
  return [!!valor, setValor];
}

export function InterruptorMostrarTodas({
  id,
  isOn,
  onToggle,
}: {
  /** Id del `<input>` (único en la página). */
  id: string;
  isOn: boolean;
  onToggle: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        minHeight: 44,
        marginTop: 8,
        padding: '0 4px',
        flexShrink: 0,
      }}
    >
      <label
        htmlFor={id}
        style={{ ...texto.secundario, flex: 1, color: color.fg2 }}
      >
        <Trans>Show all categories</Trans>
      </label>
      <Toggle
        id={id}
        isOn={isOn}
        onToggle={onToggle}
        aria-label={t('Show all categories')}
      />
    </View>
  );
}

/** Añade una línea (párrafo aparte) al aviso, si la hay. */
export function conLinea(mensaje: string, linea: string | null): string {
  return linea ? `${mensaje}\n\n${linea}` : mensaje;
}

/**
 * Línea «Recuerda hacer el traspaso de <origen> a <destino>» cuando el
 * dinero pasa entre categorías de cuentas distintas; `null` si no hace
 * falta.
 */
export function useLineaTraspaso(cuentasCat: CuentasCategorias) {
  const { t } = useTranslation();
  return useCallback(
    (
      cuentaOrigen: string | null | undefined,
      cuentaDestino: string | null | undefined,
    ): string | null => {
      const traspaso = traspasoPendiente(cuentaOrigen, cuentaDestino);
      if (!traspaso) {
        return null;
      }
      const nombre = (id: string) =>
        cuentasCat.cuentas.find(c => c.id === id)?.name ?? '';
      return t('Remember to transfer from {{fromAccount}} to {{toAccount}}', {
        fromAccount: nombre(traspaso.desde),
        toAccount: nombre(traspaso.hacia),
      });
    },
    [cuentasCat, t],
  );
}
